import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';

import {
  buildDispatchRatingRequest,
  cloneDispatchFreightDraft,
  createEmptyDispatchFreightDraft,
  validateDispatchFreightDraft,
} from '../../domain/dispatch-freight.builder';
import {
  DispatchCatalogFreightLineDraft,
  DispatchFreightContext,
  DispatchFreightDraft,
  DispatchFreightLineDraft,
  DispatchFreightValidationIssue,
  DispatchManualFreightLineDraft,
  DispatchMeasurementUnit,
  DispatchPreferredRateMethod,
  DispatchRateQuoteResponse,
  DispatchRateUiState,
  DispatchRatingRequest,
  DispatchWeightUnit,
} from '../../models/dispatch-freight.models';

type WorkbenchMode = 'edit' | 'rate';
type LineNumberField = 'package_quantity' | 'length' | 'width' | 'height' | 'weight';
type LineStringField = 'label' | 'customer_product_uuid';
type LineBooleanField = 'stackable' | 'floor_rotation_allowed';
type FreightNumberField =
  | 'total_weight_lb'
  | 'linear_feet'
  | 'handling_units'
  | 'cubic_feet'
  | 'density_lb_per_cuft'
  | 'delivery_type';

const EMPTY_CONTEXT: DispatchFreightContext = { origin: {}, destination: {} };

@Component({
  selector: 'app-dispatch-freight-workbench',
  templateUrl: './dispatch-freight-workbench.component.html',
  styleUrls: ['./dispatch-freight-workbench.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DispatchFreightWorkbenchComponent {
  @Input() mode: WorkbenchMode = 'edit';
  @Input() context: DispatchFreightContext = EMPTY_CONTEXT;
  @Input() preferredRateMethod: DispatchPreferredRateMethod | null = null;
  @Input() requireCustomer = true;
  @Input() rateState: DispatchRateUiState = 'idle';
  @Input() rateResponse: DispatchRateQuoteResponse | null = null;
  @Input() rateError = '';

  @Input()
  set draft(value: DispatchFreightDraft | null | undefined) {
    this.internalDraft = cloneDispatchFreightDraft(value ?? createEmptyDispatchFreightDraft());
    this.prepared = false;
  }
  get draft(): DispatchFreightDraft {
    return this.internalDraft;
  }

  @Output() readonly draftChange = new EventEmitter<DispatchFreightDraft>();
  @Output() readonly rateRequested = new EventEmitter<DispatchRatingRequest>();

  prepared = false;
  showValidation = false;
  internalDraft = createEmptyDispatchFreightDraft();
  private lineSequence = 0;

  get effectiveDraft(): DispatchFreightDraft {
    if (
      this.internalDraft.freight.preferred_rate_method !== null ||
      this.preferredRateMethod === null
    ) {
      return this.internalDraft;
    }

    return {
      ...this.internalDraft,
      freight: {
        ...this.internalDraft.freight,
        preferred_rate_method: this.preferredRateMethod,
      },
    };
  }

  get validationIssues(): DispatchFreightValidationIssue[] {
    return validateDispatchFreightDraft(this.effectiveDraft, this.context, {
      requireCustomer: this.requireCustomer,
    });
  }

  get readyForRating(): boolean {
    return this.validationIssues.length === 0;
  }

  get manualCount(): number {
    return this.internalDraft.items.filter((item) => item.source === 'manual').length;
  }

  get catalogCount(): number {
    return this.internalDraft.items.filter((item) => item.source === 'catalog').length;
  }

  get originLabel(): string {
    return this.locationLabel(this.context.origin);
  }

  get destinationLabel(): string {
    return this.locationLabel(this.context.destination);
  }

  get activeRateMethod(): DispatchPreferredRateMethod | null {
    return this.effectiveDraft.freight.preferred_rate_method;
  }

  get rateTotalLabel(): string {
    const total = this.rateResponse?.quote.total;
    if (!total) return 'Not calculated';
    return `${total.currency} ${total.amount.toFixed(2)}`;
  }

  get rateMethodLabel(): string {
    return this.rateResponse?.selected_rate?.rate_method_label || 'Not selected';
  }

  get ratingReadinessMessage(): string {
    if (this.readyForRating) {
      return this.rateState === 'rated' ? 'Customer rate ready.' : 'Ready to get a customer rate.';
    }

    const missingShipment = this.validationIssues.some((issue) =>
      issue.path === 'customer_contact_uuid'
      || issue.path === 'origin'
      || issue.path === 'destination',
    );
    if (missingShipment && this.internalDraft.items.length === 0) {
      return 'Add freight and finish the shipment above.';
    }
    if (missingShipment) return 'Finish the shipment above to price this freight.';
    if (this.internalDraft.items.length === 0) return 'Add freight to continue.';
    if (this.validationIssues.some((issue) => issue.path.startsWith('freight_items.'))) {
      return 'Complete the highlighted freight details.';
    }
    return 'Check the rating options.';
  }

  get originZoneLabel(): string {
    const zone = this.rateResponse?.lane.origin_zone
      || this.rateResponse?.selected_rate?.origin_zone;
    return zone?.zone_name || zone?.name || zone?.zone_id || 'Unresolved';
  }

  get destinationZoneLabel(): string {
    const zone = this.rateResponse?.lane.destination_zone
      || this.rateResponse?.selected_rate?.destination_zone;
    return zone?.zone_name || zone?.name || zone?.zone_id || 'Unresolved';
  }

  addManualLine(): void {
    const line: DispatchManualFreightLineDraft = {
      id: this.nextLineId('manual'),
      source: 'manual',
      label: '',
      package_quantity: 1,
      length: null,
      width: null,
      height: null,
      weight: null,
      measurement_unit: 'IN',
      weight_unit: 'LB',
      stackable: false,
      floor_rotation_allowed: false,
    };
    this.commit({ ...this.internalDraft, items: [...this.internalDraft.items, line] });
  }

  addCatalogLine(): void {
    const line: DispatchCatalogFreightLineDraft = {
      id: this.nextLineId('catalog'),
      source: 'catalog',
      customer_product_uuid: '',
      package_quantity: 1,
      floor_rotation_allowed: false,
    };
    this.commit({ ...this.internalDraft, items: [...this.internalDraft.items, line] });
  }

  removeLine(index: number): void {
    this.commit({
      ...this.internalDraft,
      items: this.internalDraft.items.filter((_, itemIndex) => itemIndex !== index),
    });
  }

  updateLineNumber(index: number, field: LineNumberField, event: Event): void {
    this.patchLine(index, { [field]: this.numberFromEvent(event) });
  }

  updateLineString(index: number, field: LineStringField, event: Event): void {
    this.patchLine(index, { [field]: this.stringFromEvent(event) });
  }

  updateLineBoolean(index: number, field: LineBooleanField, event: Event): void {
    this.patchLine(index, { [field]: this.checkedFromEvent(event) });
  }

  updateMeasurementUnit(index: number, event: Event): void {
    const value = this.stringFromEvent(event) as DispatchMeasurementUnit;
    if (value === 'IN' || value === 'CM') {
      this.patchLine(index, { measurement_unit: value });
    }
  }

  updateWeightUnit(index: number, event: Event): void {
    const value = this.stringFromEvent(event) as DispatchWeightUnit;
    if (value === 'LB' || value === 'KG') {
      this.patchLine(index, { weight_unit: value });
    }
  }

  updateFreightNumber(field: FreightNumberField, event: Event): void {
    this.commit({
      ...this.internalDraft,
      freight: {
        ...this.internalDraft.freight,
        [field]: this.numberFromEvent(event),
      },
    });
  }

  updateRateMethod(event: Event): void {
    const numeric = Number(this.stringFromEvent(event));
    const value: DispatchPreferredRateMethod | null =
      numeric === 1 || numeric === 2 || numeric === 3 || numeric === 4 ? numeric : null;
    this.commit({
      ...this.internalDraft,
      freight: { ...this.internalDraft.freight, preferred_rate_method: value },
    });
  }

  updateLoadType(event: Event): void {
    this.commit({
      ...this.internalDraft,
      freight: { ...this.internalDraft.freight, load_type: this.stringFromEvent(event) },
    });
  }

  prepareRateRequest(): void {
    this.showValidation = true;
    if (!this.readyForRating) return;

    const request = buildDispatchRatingRequest(this.effectiveDraft, this.context, {
      requireCustomer: this.requireCustomer,
    });
    this.showValidation = false;
    this.prepared = true;
    this.rateRequested.emit(request);
  }

  manualLine(line: DispatchFreightLineDraft): DispatchManualFreightLineDraft {
    return line as DispatchManualFreightLineDraft;
  }

  catalogLine(line: DispatchFreightLineDraft): DispatchCatalogFreightLineDraft {
    return line as DispatchCatalogFreightLineDraft;
  }

  trackLine(_index: number, line: DispatchFreightLineDraft): string {
    return line.id;
  }

  lineIssueCount(index: number): number {
    const prefix = `freight_items.${index}`;
    return this.validationIssues.filter((issue) => issue.path.startsWith(prefix)).length;
  }

  hasIssue(path: string): boolean {
    return this.validationIssues.some((issue) => issue.path === path);
  }

  private patchLine(index: number, patch: Record<string, unknown>): void {
    const items = this.internalDraft.items.map((line, itemIndex) =>
      itemIndex === index ? ({ ...line, ...patch } as DispatchFreightLineDraft) : line,
    );
    this.commit({ ...this.internalDraft, items });
  }

  private commit(draft: DispatchFreightDraft): void {
    this.internalDraft = cloneDispatchFreightDraft(draft);
    this.prepared = false;
    this.draftChange.emit(cloneDispatchFreightDraft(this.internalDraft));
  }

  private nextLineId(source: 'manual' | 'catalog'): string {
    this.lineSequence += 1;
    return `dispatch-${source}-${Date.now()}-${this.lineSequence}`;
  }

  private numberFromEvent(event: Event): number | null {
    const value = (event.target as HTMLInputElement | null)?.value.trim() ?? '';
    if (!value) return null;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  private stringFromEvent(event: Event): string {
    return (event.target as HTMLInputElement | HTMLSelectElement | null)?.value ?? '';
  }

  private checkedFromEvent(event: Event): boolean {
    return (event.target as HTMLInputElement | null)?.checked ?? false;
  }

  private locationLabel(location: DispatchFreightContext['origin']): string {
    const postal = location.postal_code?.trim();
    if (postal) return postal.toUpperCase();

    return [location.city, location.province]
      .map((part) => part?.trim())
      .filter((part): part is string => Boolean(part))
      .join(', ') || 'Not set';
  }
}
